import { memo, useMemo } from 'react';
import { Text } from '@react-three/drei';
import * as THREE from 'three';
import type { TacticsZone } from '@4cm/core/tactics';

/**
 * Renders the Phase 3 coloured zones as flat meshes on the field at
 * y=0.005 — just above the grass plane but below the player tokens
 * (y=0.6+) so coaches see zones tinted into the surface without
 * blotting out the markings.
 *
 * - rect → planeGeometry rotated to lie on XZ.
 * - circle → circleGeometry rotated to lie on XZ.
 * - polygon → ShapeGeometry built from the vertices.
 *
 * Materials are `MeshStandardMaterial` (transparent, opacity 0.18, no
 * shadows). Labels use `<Text>` from drei billboarded so they stay
 * readable from any camera angle, lifted to y=0.02 to clear the zone
 * surface.
 */
export interface ZoneLayer3DProps {
  zones: TacticsZone[] | undefined;
}

// Mirror the 2D layer's baseline opacity so cross-surface parity holds.
const ZONE_OPACITY = 0.18;
const ZONE_Y = 0.005;
const LABEL_Y = 0.02;
const LABEL_COLOR = '#f5f5f5';
const LABEL_OUTLINE_COLOR = '#04140b';

function zoneCentroidWorld(zone: TacticsZone): { x: number; z: number } | null {
  if (zone.shape === 'rect' || zone.shape === 'circle') {
    const cx = zone.geom[0];
    const cz = zone.geom[1];
    if (typeof cx !== 'number' || typeof cz !== 'number') return null;
    return { x: cx, z: cz };
  }
  if (zone.shape === 'polygon') {
    let sx = 0;
    let sz = 0;
    let n = 0;
    for (let i = 0; i < zone.geom.length; i += 2) {
      const wx = zone.geom[i];
      const wz = zone.geom[i + 1];
      if (typeof wx !== 'number' || typeof wz !== 'number') continue;
      sx += wx;
      sz += wz;
      n += 1;
    }
    if (n === 0) return null;
    return { x: sx / n, z: sz / n };
  }
  return null;
}

interface ZoneMeshProps {
  zone: TacticsZone;
}

function ZoneMesh({ zone }: ZoneMeshProps) {
  // Build geometry once per zone shape — the shape data is content-
  // addressable on geom values, so a coach dragging a different prop
  // doesn't rebuild Three.js objects every frame.
  const geometry = useMemo(() => {
    if (zone.shape === 'rect') {
      const w = zone.geom[2];
      const d = zone.geom[3];
      if (typeof w !== 'number' || typeof d !== 'number') return null;
      return new THREE.PlaneGeometry(w, d);
    }
    if (zone.shape === 'circle') {
      const r = zone.geom[2];
      if (typeof r !== 'number') return null;
      return new THREE.CircleGeometry(r, 48);
    }
    if (zone.shape === 'polygon') {
      // ShapeGeometry from the polygon vertices, projected to local XY
      // (the mesh is rotated -π/2 around X so local Y maps to world Z).
      const centroid = zoneCentroidWorld(zone);
      if (!centroid) return null;
      const shape = new THREE.Shape();
      let started = false;
      for (let i = 0; i < zone.geom.length; i += 2) {
        const wx = zone.geom[i];
        const wz = zone.geom[i + 1];
        if (typeof wx !== 'number' || typeof wz !== 'number') return null;
        // Local coords: shift to centroid-relative so the mesh's position
        // prop places the shape correctly.
        const lx = wx - centroid.x;
        const ly = wz - centroid.z;
        if (!started) {
          shape.moveTo(lx, ly);
          started = true;
        } else {
          shape.lineTo(lx, ly);
        }
      }
      if (!started) return null;
      return new THREE.ShapeGeometry(shape);
    }
    return null;
  }, [zone]);

  if (!geometry) return null;

  const centroid = zoneCentroidWorld(zone);
  if (!centroid) return null;

  // The mesh sits on the XZ plane — flat against the grass. Rotating
  // -π/2 around X aligns the geometry's XY plane with the world's XZ.
  return (
    <mesh
      position={[centroid.x, ZONE_Y, centroid.z]}
      rotation={[-Math.PI / 2, 0, 0]}
      receiveShadow={false}
      castShadow={false}
      // Block raycasts — zones are decorative; tapping a zone should
      // fall through to the empty-pitch click target (selection clear).
      raycast={() => undefined}
    >
      <primitive object={geometry} attach="geometry" />
      <meshStandardMaterial
        color={zone.fill}
        transparent
        opacity={ZONE_OPACITY}
        roughness={0.95}
        metalness={0}
        // Disable depth-write so multiple overlapping zones blend
        // additively rather than fighting for the same pixel.
        depthWrite={false}
        side={THREE.DoubleSide}
      />
    </mesh>
  );
}

function ZoneLayer3DInner({ zones }: ZoneLayer3DProps) {
  if (!zones || zones.length === 0) return null;
  return (
    <group name="zone-layer">
      {zones.map((z) => {
        const centroid = zoneCentroidWorld(z);
        return (
          <group key={z.id}>
            <ZoneMesh zone={z} />
            {z.label && centroid && (
              <Text
                position={[centroid.x, LABEL_Y, centroid.z]}
                rotation={[-Math.PI / 2, 0, 0]}
                fontSize={1.6}
                color={LABEL_COLOR}
                outlineColor={LABEL_OUTLINE_COLOR}
                outlineWidth={0.04}
                anchorX="center"
                anchorY="middle"
                // Billboard isn't required for floor-level text — the
                // -π/2 X rotation keeps the label flat on the pitch
                // which reads naturally with the camera's elevated
                // angle. (Coaches read pitch diagrams in plan view.)
              >
                {z.label}
              </Text>
            )}
          </group>
        );
      })}
    </group>
  );
}

export const ZoneLayer3D = memo(ZoneLayer3DInner);
